import {
  Box,
  Button,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import StyledTableCell from 'components/atoms/Table/StylizedTableCell.style'
import StyledTableRow from 'components/atoms/Table/StylizedTableRow.style'
import TableRowsLoader from 'components/atoms/Table/TableLoader.component'
import { Text } from 'components/atoms/Text/Text'
import { PageContainer } from 'components/organisms/PageContainer/PageContainer'
import { toInteger } from 'lodash'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { useGetPostGroupPostQuery, useGetPostGroupsQuery } from 'store/apis/posts/groupPostsApi'
import { useGetPostsQuery } from 'store/apis/posts/postsApi'
import { TPostFilter } from 'store/apis/posts/types'

const DEFAULT_FORM_VALUES: TPostFilter = {
  title: '',
  postGroupId: 0,
  draft: false,
}

const ArchivePosts = () => {
  const { getValues, control, handleSubmit } = useForm<TPostFilter>({
    defaultValues: DEFAULT_FORM_VALUES,
    shouldUnregister: true,
  })

  const [page, setPage] = useState<number>(1)

  const { data: postGroups, isFetching: isPostGroupsFetching } = useGetPostGroupsQuery()

  const postGroupId = getValues().postGroupId

  const {
    refetch: refetchUserPosts,
    data: userPosts,
    isFetching: isUserPostsFetching,
  } = useGetPostsQuery({ ...getValues(), page }, { skip: postGroupId !== 0 })

  const {
    refetch: refetchPostGroupPosts,
    data: postGroupPosts,
    isFetching: isPostGroupsPostsFetching,
  } = useGetPostGroupPostQuery(
    { ...getValues(), id: postGroupId, page },
    { skip: postGroupId === 0 },
  )

  const data = useMemo(() => {
    return postGroupId !== 0 ? postGroupPosts : userPosts
  }, [userPosts, postGroupPosts])

  const refetch = useMemo(() => {
    return postGroupId !== 0 ? refetchPostGroupPosts : refetchUserPosts
  }, [userPosts, postGroupPosts])

  const posts =
    useMemo(() => {
      if (data && postGroups) {
        return data.posts.map((post) => {
          return {
            ...post,
            postGroupName: postGroups.find((pg) => pg.id === post.postGroupId)?.name,
          }
        })
      }
    }, [data, postGroups]) || []

  const isFetching = isUserPostsFetching || isPostGroupsPostsFetching || isPostGroupsFetching

  const onPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  const onSubmitHandler = () => {
    setPage(1)
    refetch()
  }

  return (
    <PageContainer>
      <form
        onSubmit={handleSubmit(onSubmitHandler)}
        style={{ marginBottom: '30px', display: 'flex', flexDirection: 'row', gap: '10px' }}
      >
        <Controller
          name='title'
          control={control}
          render={({ field }) => (
            <TextField {...field} sx={{ flex: 1 }} placeholder='Filter by title' />
          )}
        />

        <Controller
          name='postGroupId'
          control={control}
          render={({ field }) => (
            <Select {...field} displayEmpty>
              <MenuItem key='all' value={0}>
                All
              </MenuItem>
              {postGroups?.map((postGroup) => (
                <MenuItem key={postGroup.id} value={postGroup.id}>
                  {postGroup.name}
                </MenuItem>
              ))}
            </Select>
          )}
        />

        <Button type='submit' variant='contained' sx={{ marginLeft: '10px' }} disabled={isFetching}>
          Filter
        </Button>
      </form>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Title</StyledTableCell>
              <StyledTableCell>Drawer</StyledTableCell>
              <StyledTableCell align='left'>Published At</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRowsLoader rowsNum={10} />
            ) : posts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Text textAlign='center'>No records found</Text>
                </TableCell>
              </TableRow>
            ) : (
              posts.map((post) => (
                <StyledTableRow
                  key={post.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <StyledTableCell component='th' scope='row'>
                    <Link to={`/posts/${post.key}`} style={{ color: 'black' }}>
                      {post.title}
                    </Link>
                  </StyledTableCell>
                  <StyledTableCell component='th' scope='row'>
                    {post.postGroupName}
                  </StyledTableCell>
                  <StyledTableCell align='left'>
                    {post.publishedAt?.toLocaleString(DateTime.DATETIME_SHORT)}
                  </StyledTableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display='flex' justifyContent='end' margin='20px 0'>
        <Pagination
          count={data ? toInteger(data.totalPages) : 0}
          page={page}
          onChange={onPageChange}
        />
      </Box>
    </PageContainer>
  )
}

export default ArchivePosts
